<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header full-height-page-wrapper">
          <PageHeader showBreadcrumbs :page-title="'Contractors Telegram Campaigns'">
            <template v-slot:buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                variant="success"
                class="button"
                @click="() => showModal()"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="getTableFilters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            @submit="setFilters"
          />

          <div v-if="isDataPageLoading && !isInitialDataLoaded" class="content-loading-wrapper">
            <Preloader class="centered-preloader" />
          </div>

          <div v-else class="full-height-content">
            <TableForPage
              v-if="getTableData.length > 0"
              ref="table"
              :isDataLoading="isDataPageLoading && isInitialDataLoaded"
              :tableData="getTableData"
              :fields="getTableFields"
              :slots="getTableSlots"
              :perPage="getContractorTelegramCampaigns.per_page"
              :currentPage="getContractorTelegramCampaigns.current_page"
              :totalRows="getContractorTelegramCampaigns.total"
              :actions="getTableActions"
              isServerRender
              noLocalSorting
              @remove="remove"
              @edit="showModal"
              @start="start"
              @update="updateList"
            >
              <template v-slot:contractors="data">
                <span v-if="data.item.for_all">For All</span>
                <span v-else-if="data.item.contractors.length === 0">Selected telegram was removed</span>
                <template v-else>
                  <div v-for="telegram in data.item.contractors" v-bind:key="telegram.id">
                    <span :key="telegram.id">{{ telegram.user.name }}</span>
                  </div>
                </template>
              </template>
            </TableForPage>
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getTableData.length"
              title="No campaigns for contractors"
              description="Create a campaign to send updates and information to your contractors."
              buttonText="Create campaign"
              customIcon="account_circle"
              customIconClasses="material-symbols-outlined empty-state__icon"
              @button-click="() => showModal()"
            />
          </div>
        </div>
      </div>
    </div>

    <ContractorTelegramCampaignModal
      v-model="modal.show"
      :form="modal.form"
      :isLoading="isModalLoading"
      @save="save"
    />
  </div>
</template>

<script>
import PageHeader from '@components/PageHeader'
import Filters from '@components/Filter/Filter'
import TableForPage from '@tables/TableForPage'
import { mapGetters } from 'vuex'
import TelegramContractorsCampaignMixin from '@mixins/notifications/TelegramContractorsCampaignMixin'
import ContractorTelegramCampaignModal from '@components/modals/Telegram/ContractorTelegramCampaignModal'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'

export default {
  mixins: [TelegramContractorsCampaignMixin],
  components: {
    PageHeader,
    ContractorTelegramCampaignModal,
    Filters,
    TableForPage,
    Preloader,
    EmptyStateNoResults
  },
  data () {
    return {
      isAllOptionsFilterSet: false,
      isTelegramContractorCampaignDataLoaded: false,
      isModalLoading: false
    }
  },
  created () {
    this.fetchAllData()
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isTelegramContractorCampaignDataLoaded
    },
    ...mapGetters({
      getAvailableContractorTelegrams: 'telegram/getAvailableContractorTelegrams',
      getContractorTelegramCampaigns: 'telegram/getContractorTelegramCampaigns'
    }),
    getTableFilters () {
      return this.filters || {}
    },
    getTableFields () {
      return this.table.fields || []
    },
    getTableSlots () {
      return this.table.slots || []
    },
    getTableActions () {
      return this.table.actions || []
    },
    getTableData () {
      return this.getContractorTelegramCampaigns.data || []
    }
  },
  methods: {
    fetchAllData () {
      return Promise.all([
        this.fetchAvailableContractors(),
        this.fetchList()
      ]).then(() => {
        this.setAllOptions()
      })
    },
    setAllOptions () {
      this.modal.form.contractors.options = this.getAvailableContractorTelegrams
      this.isAllOptionsFilterSet = true
    },
    fetchAvailableContractors () {
      return this.$api.telegram.getAvailableContractorTelegrams()
    },
    fetchList () {
      let params = this.table.params.currentPage
      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }
      return this._toggleDataPageLoading(() => this.$api.telegram.getContractorTelegramCampaigns(params)).then((result) => {
        this.isTelegramContractorCampaignDataLoaded = true
        return result
      })
    },
    updateList (data) {
      if (!data) return this.fetchList()

      const { currentPage, sortDesc, sortBy } = data
      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)
      this.fetchList()
    },
    setFilters (data) {
      this.setParamsFilters(data)
      this.setParamsCurrentPage()
      this.fetchList()
    },
    save (data) {
      this.isModalLoading = true
      const campaign = this.prepareDataForRequest(data)
      const requestPromise = this.modal.campaignId
        ? this.$api.telegram.editContractorTelegramCampaign({ id: this.modal.campaignId, ...campaign })
        : this.$api.telegram.createContractorTelegramCampaign(campaign)
      requestPromise.then(() => {
        this.$snotify.success('Contractor telegram campaign successfully saved', 'Success', this.$store.state.snotify)
        this.hideModal()
        this.fetchList()
      }).catch(err => {
        this.errorNotifications(err)
      })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    async start (item) {
      const confirm = await this.$root.$confirm.open(
        'Start',
        'Are you sure you want to start the contractor telegram campaign?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        const startResponse = await this.$api.telegram.startContractorTelegramCampaign(item.id)
        this.$snotify.success(
          startResponse.data,
          'Success',
          this.$store.state.snotify
        )
      })
    },
    async remove (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the contractor telegram campaign?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        const deleteResponse = await this.$api.telegram.removeContractorTelegramCampaign(item.id)
        this.$snotify.success(
          deleteResponse.data,
          'Success',
          this.$store.state.snotify
        )
        this.updateList()
      })
    }
  }
}
</script>
